import React, { useRef, useState, useEffect } from "react";
import NavItem from "../atoms/NavItem";
import IconRenderer from "../atoms/IconRenderer";
import { ChevronDown } from "lucide-react";
import { useDropdownNavigation } from "@/hooks";

const NavDropdown = ({
  icon,
  iconType,
  isWhite = false,
  label,
  children,
  isExpanded = true,
  className = "",
  dropdownId,
  items = [],
  ...props
}) => {
  const { isOpen, hasActiveChild, handleToggle } =
    useDropdownNavigation(dropdownId, items);

  const [showFlyout, setShowFlyout] = useState(false);
  const flyoutRef = useRef(null);
  const triggerRef = useRef(null);

  // Flyout for collapsed sidebar
  useEffect(() => {
    if (isExpanded) setShowFlyout(false);
  }, [isExpanded]);

  const getActiveClasses = () => {
    if (hasActiveChild) {
      return "bg-orange-500/15 text-orange-400";
    }
    if (isOpen && isExpanded) {
      return "bg-white/5 text-gray-200";
    }
    return "text-gray-400 hover:bg-white/5 hover:text-gray-200";
  };

  const handleClick = () => {
    if (isExpanded) {
      handleToggle();
    }
  };

  return (
    <div
      className={`relative ${className}`}
      ref={triggerRef}
      onMouseEnter={() => !isExpanded && setShowFlyout(true)}
      onMouseLeave={() => !isExpanded && setShowFlyout(false)}
    >
      <NavItem
        isActive={false}
        isExpanded={isExpanded}
        onClick={handleClick}
        title={label}
        className={getActiveClasses()}
        {...props}
      >
        <IconRenderer
          icon={icon}
          iconType={iconType}
          isWhite={isWhite}
          size="md"
          className={isExpanded ? "mr-3" : ""}
        />
        {isExpanded && <span className="truncate">{label}</span>}
        {isExpanded && (
          <ChevronDown
            size={14}
            className={`ml-auto transition-transform duration-200 ${
              isOpen ? "rotate-180" : ""
            }`}
          />
        )}
      </NavItem>

      {/* Expanded: inline dropdown */}
      {isExpanded && (
        <div
          className={`overflow-hidden transition-all duration-200 ease-in-out ${
            isOpen ? "max-h-96 opacity-100" : "max-h-0 opacity-0"
          }`}
        >
          <ul className="ml-4 border-l border-gray-700 pl-0 py-1">
            {children}
          </ul>
        </div>
      )}

      {/* Collapsed: flyout menu */}
      {!isExpanded && showFlyout && (
        <div
          ref={flyoutRef}
          className="absolute left-full top-0 ml-2 bg-gray-800 border border-gray-700 rounded-lg shadow-xl py-2 min-w-[200px] z-50"
        >
          <div className="px-3 py-1.5 text-xs font-semibold text-gray-500 uppercase tracking-wider">
            {label}
          </div>
          <ul>{children}</ul>
        </div>
      )}
    </div>
  );
};

export default NavDropdown;
